import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import type { Meal, MealIngredient, SideDish } from '@meal-planner/shared';
import { api } from '../services/api.js';
import { Icon } from './ui/Icon.js';
import { Placeholder, type PlaceholderTone } from './ui/Placeholder.js';

interface FavoriteRecipe { id: string; recipeId: string; }

interface BudgetSwapInfo {
  originalName?: string;
  weeklyBudget: number | null;
}

interface Props {
  meal: Meal | null;
  onClose: () => void;
  // G2 Area 3 — present when this meal was auto-swapped to fit budget
  budgetSwapInfo?: BudgetSwapInfo;
}

const MEAL_TYPE_LABEL: Record<string, string> = {
  breakfast: 'Breakfast',
  lunch: 'Lunch',
  dinner: 'Dinner',
};

const MEAL_TONE: Record<string, PlaceholderTone> = {
  breakfast: 'warm',
  lunch: 'leaf',
  dinner: 'spice',
};

const METHOD_LABEL: Record<string, string> = {
  stovetop:    'Stovetop',
  oven:        'Oven',
  air_fryer:   'Air fryer',
  instant_pot: 'Instant Pot',
  slow_cooker: 'Slow cooker',
  microwave:   'Microwave',
  grill:       'Grill',
};

const DIFFICULTY_CONFIG = {
  easy: {
    label: 'Easy',
    style: { background: 'var(--accent-soft)', color: 'var(--accent)' },
  },
  intermediate: {
    label: 'Medium',
    style: { background: 'color-mix(in oklab, var(--warning) 18%, transparent)', color: 'var(--warning)' },
  },
  expert: {
    label: 'Hard',
    style: { background: 'color-mix(in oklab, var(--danger) 18%, transparent)', color: 'var(--danger)' },
  },
} as const;

function HeartButton({ recipeId, recipeName, mealType }: { recipeId: string; recipeName: string; mealType: string }) {
  const qc = useQueryClient();
  const { data: favorites = [] } = useQuery<FavoriteRecipe[]>({
    queryKey: ['favorites'],
    queryFn: () => api.get('/api/favorites'),
    staleTime: 5 * 60_000,
  });
  const isFav = favorites.some((f) => f.recipeId === recipeId);
  const add = useMutation({
    mutationFn: () => api.post('/api/favorites', { recipeId, recipeName, mealType }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['favorites'] }),
  });
  const remove = useMutation({
    mutationFn: () => api.delete(`/api/favorites/${recipeId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['favorites'] }),
  });

  return (
    <button
      onClick={() => isFav ? remove.mutate() : add.mutate()}
      disabled={add.isPending || remove.isPending}
      aria-label={isFav ? 'Remove from favorites' : 'Add to favorites'}
      className="w-10 h-10 rounded-full flex items-center justify-center transition-colors disabled:opacity-50"
      style={{
        background: isFav ? 'color-mix(in oklab, var(--danger) 18%, transparent)' : 'var(--surface-2)',
        color: isFav ? 'var(--danger)' : 'var(--ink-3)',
      }}>
      <Icon name="heart" size={16} stroke={isFav ? 2.4 : 1.7} />
    </button>
  );
}

function IngredientList({ ingredients }: { ingredients: MealIngredient[] }) {
  return (
    <ul className="flex flex-wrap gap-1.5">
      {ingredients.map((ing, i) => (
        <li key={i} className="inline-flex items-center text-xs rounded-full px-2.5 py-1"
          style={{
            background: 'var(--surface-2)',
            color: 'var(--ink-2)',
            border: '1px solid var(--line-soft)',
          }}>
          <span>{ing.quantity > 0 ? `${ing.quantity} ` : ''}{ing.unit} {ing.name}</span>
        </li>
      ))}
    </ul>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-[11px] font-semibold uppercase mb-2"
      style={{ color: 'var(--ink-4)', letterSpacing: '0.14em' }}>
      {children}
    </h3>
  );
}

function SideDishSection({ side }: { side: SideDish }) {
  return (
    <section>
      <div className="flex items-center gap-2 mb-3">
        <span className="text-[10px] font-bold uppercase shrink-0"
          style={{ color: 'var(--ink-4)', letterSpacing: '0.16em' }}>Side</span>
        <div className="flex-1 h-px" style={{ background: 'var(--line-soft)' }} />
      </div>
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <h4 className="h-serif" style={{ fontSize: 18, color: 'var(--ink)', margin: 0 }}>{side.name}</h4>
        {side.cookTimeMins && (
          <span className="chip" style={{ fontSize: 11.5 }}>
            <Icon name="clock" size={11} /> {side.cookTimeMins} min
          </span>
        )}
      </div>
      <SectionLabel>Ingredients</SectionLabel>
      <div className="mb-4">
        <IngredientList ingredients={side.ingredients} />
      </div>
      <SectionLabel>Instructions</SectionLabel>
      <ol className="list-decimal list-inside flex flex-col gap-1.5">
        {side.instructions.map((step, i) => (
          <li key={i} className="text-sm leading-relaxed" style={{ color: 'var(--ink-2)' }}>{step}</li>
        ))}
      </ol>
    </section>
  );
}

export function RecipeModal({ meal, onClose, budgetSwapInfo }: Props) {
  const navigate = useNavigate();
  const location = useLocation();

  if (!meal) return null;

  const diffConfig = meal.difficulty ? DIFFICULTY_CONFIG[meal.difficulty] : null;
  const tone = MEAL_TONE[meal.type] ?? 'warm';

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40"
        onClick={onClose}
        aria-hidden="true"
        style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(8px)' }}
      />

      {/* Modal panel */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={meal.name}
        className="fixed inset-x-4 top-4 bottom-4 md:inset-x-auto md:left-1/2 md:-translate-x-1/2 md:w-full md:max-w-xl z-50 flex flex-col overflow-hidden"
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--line-soft)',
          borderRadius: 'var(--r-lg)',
          boxShadow: 'var(--shadow-pop)',
        }}
      >
        {/* Hero placeholder */}
        <div className="relative shrink-0">
          <Placeholder tone={tone} label={meal.name.toLowerCase()} style={{ height: 140, borderRadius: 0 }} />
          <button
            onClick={onClose}
            aria-label="Close recipe"
            className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center transition-colors"
            style={{
              background: 'rgba(0,0,0,0.45)',
              color: 'oklch(0.97 0.012 80)',
              backdropFilter: 'blur(8px)',
            }}
          >
            <Icon name="x" size={16} stroke={2.2} />
          </button>
        </div>

        {/* Header */}
        <div className="px-6 pt-5 pb-4 shrink-0" style={{ borderBottom: '1px solid var(--line-soft)' }}>
          <div className="flex items-start gap-3">
            <div className="flex-1 min-w-0">
              <span className="inline-block text-[10px] font-bold tracking-widest uppercase rounded-full px-2 py-0.5 mb-2"
                style={{ background: 'var(--accent-soft)', color: 'var(--accent)' }}>
                {MEAL_TYPE_LABEL[meal.type] ?? meal.type}
              </span>
              <h2 className="h-serif" style={{ fontSize: 24, color: 'var(--ink)', lineHeight: 1.2, margin: 0 }}>
                {meal.name}
              </h2>
              {meal.tagline && (
                <p className="mt-1.5 text-sm italic" style={{ color: 'var(--ink-3)' }}>{meal.tagline}</p>
              )}
              <div className="flex items-center gap-2 mt-3 flex-wrap">
                {diffConfig && (
                  <span className="text-[11px] font-semibold rounded-full px-2.5 py-1" style={diffConfig.style}>
                    {diffConfig.label}
                  </span>
                )}
                {meal.cookTimeMins && (
                  <span className="chip" style={{ fontSize: 11.5 }}>
                    <Icon name="clock" size={11} /> {meal.cookTimeMins} min
                  </span>
                )}
                {meal.cookingMethod && meal.cookingMethod.map((m) => (
                  <span key={m} className="text-[11px] font-medium rounded-full px-2.5 py-1"
                    style={{
                      background: 'color-mix(in oklab, var(--accent) 14%, var(--surface))',
                      color: 'var(--ink-2)',
                    }}>
                    {METHOD_LABEL[m] ?? m}
                  </span>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {meal.recipeId && (
                <HeartButton recipeId={meal.recipeId} recipeName={meal.name} mealType={meal.type} />
              )}
            </div>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-6">
          {budgetSwapInfo && (
            <div
              className="rounded-lg px-4 py-3 text-sm"
              style={{
                background: 'var(--accent-soft)',
                color: 'var(--ink-2)',
                border: '1px solid color-mix(in oklab, var(--accent) 25%, transparent)',
              }}
            >
              <span className="font-semibold" style={{ color: 'var(--accent)' }}>
                {budgetSwapInfo.originalName ?? 'The original recipe'}
              </span>{' '}
              was swapped to keep your plan within your{' '}
              {typeof budgetSwapInfo.weeklyBudget === 'number'
                ? `$${budgetSwapInfo.weeklyBudget}`
                : 'weekly'}{' '}
              weekly budget. Increase your budget in Settings to unlock it.
            </div>
          )}
          {meal.recipeId && (
            <button
              onClick={() => {
                onClose();
                navigate(`/recipes/${meal.recipeId}/cook/0`, {
                  state: { returnTo: location.pathname },
                });
              }}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 font-semibold text-sm"
              style={{
                background: 'var(--accent)',
                color: 'var(--bg-deep)',
                boxShadow: '0 6px 18px -6px var(--accent-glow)',
              }}
            >
              <Icon name="cookpot" size={16} stroke={2.2} />
              Start cooking
            </button>
          )}

          <section>
            <SectionLabel>Ingredients</SectionLabel>
            <IngredientList ingredients={meal.ingredients} />
          </section>

          <section>
            <SectionLabel>Instructions</SectionLabel>
            <ol className="list-decimal list-inside flex flex-col gap-2">
              {meal.instructions.map((step, i) => (
                <li key={i} className="text-sm leading-relaxed" style={{ color: 'var(--ink-2)' }}>{step}</li>
              ))}
            </ol>
          </section>

          {meal.sides && meal.sides.length > 0 && meal.sides.map((side, i) => (
            <SideDishSection key={i} side={side} />
          ))}
        </div>
      </div>
    </>
  );
}
